import { Logout } from '@modules/auth/components/Logout';
import { protectedRoutes } from '@core/routing/helper-components/Routes';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { NavItem } from './NavItem';
import { SidebarDrawer } from './SidebarDrawer';
import { Module } from '@modules/admin/types';
import { usePermission } from '@modules/auth/hooks';

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  isMobileView: boolean;
}

const Sidebar = ({ open, isMobileView, onToggle }: SidebarProps) => {
  const location = useLocation();

  // Get permissions for all modules at the top level (hooks must be called unconditionally)
  const dashboardPermission = usePermission(Module.DASHBOARD);
  const offerPermission = usePermission(Module.OFFER);
  const playbookPermission = usePermission(Module.PLAYBOOK);
  const tmcPermission = usePermission(Module.TMC);
  const abTestingPermission = usePermission(Module.AB_TESTING);
  const userPermission = usePermission(Module.USER);

  // Map modules to their permissions
  const permissionMap = {
    [Module.DASHBOARD]: dashboardPermission,
    [Module.OFFER]: offerPermission,
    [Module.PLAYBOOK]: playbookPermission,
    [Module.TMC]: tmcPermission,
    [Module.AB_TESTING]: abTestingPermission,
    [Module.USER]: userPermission,
  };

  // Helper function to check if user has permission to view a route
  const hasPermissionToView = (route: (typeof protectedRoutes)[0]): boolean => {
    // If route is public or has no module requirement, allow access
    if (route.isPublic || !route.module) {
      return true;
    }

    // Check if user has any permission for the module
    const permission = permissionMap[route.module];
    return permission?.hasAnyPermission ?? false;
  };

  // Filter routes that should be shown in the sidebar based on permissions
  const allSidebarRoutes = protectedRoutes.filter((route) => route.showInSidebar);

  // Further filter based on user permissions
  const menuItems = allSidebarRoutes.filter((route) => {
    if (route.path === '/playbook') return false; // Playbook is shown separately
    return hasPermissionToView(route);
  });

  const playbookItem = allSidebarRoutes.find((route) => route.path === '/playbook');
  const showPlaybook = playbookItem && hasPermissionToView(playbookItem);

  // To allow active menu styling as per selected route
  const isActive = (path: string) => {
    return location.pathname === path || (path !== '/' && location.pathname.startsWith(path));
  };

  const handleNavItemClick = () => {
    // Close the sidebar on mobile when an item is clicked
    if (isMobileView) {
      onToggle();
    }
  };

  return (
    <SidebarDrawer
      open={open}
      onToggle={onToggle}
      isMobileView={isMobileView}
      footerContent={
        <Box sx={{ width: '100%' }}>
          {showPlaybook && playbookItem && (
            <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
              <NavItem
                route={playbookItem}
                isOpen={open}
                isActive={isActive(playbookItem.path)}
                onClick={handleNavItemClick}
              />
            </Box>
          )}
          <Logout />
        </Box>
      }
    >
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {menuItems.map((route) => (
          <NavItem
            key={route.path}
            route={route}
            isOpen={open}
            isActive={isActive(route.path)}
            onClick={handleNavItemClick}
          />
        ))}
      </Box>
    </SidebarDrawer>
  );
};

export default Sidebar;
